import { forwardRef, useContext, useImperativeHandle, useRef, useState } from "react"
import { Button, Checkbox, Input, Modal, Spinner, WarnMesssage } from "@collegebacker/backer-ui/ui"

import { graphQLErrorHandler, isMobile, track } from "utils"
import { isEmpty } from "lodash"
import { gql, useMutation } from "@apollo/react-hooks"
import { useHistory } from "react-router-dom"
import { buildPath } from "routes"

import { SessionContext } from "context/SessionProvider"
import { emailValue, userUuidValue } from "modules/auth/LocalState"
import { authenticatedUserFragment, setRegistrationAuthenticated } from "utils/auth-utils"
import { renderLegalese } from "modules/auth/components/GetStarted"

import birthdayReminder from 'static/img/birthday-reminder.svg'
import styles from './BirthdayReminderModal.module.scss'
import { ErrorMessages, validateEmail } from "modules/auth/validation"
import { prepareRegistrationParams } from '../../../../cookie'

const CREATE_BASIC_USER = gql`
  mutation CreateBasicUser($input: CreateBasicUserInputType!) {
    createBasicUser(input: $input) {
      webAuthToken
      user {
        ...userFragment
      }
    }
  }
  ${authenticatedUserFragment}
`

const FOLLOW_FUND = gql`
  mutation FollowFund($input: FollowFundInputType!) {
    followFund(input: $input) {
      id
      handle
      uuid
    }
  }
`

const BirthdayReminderModal = forwardRef(
  ({
    handle,
    refetchCurrentUser
  }: {
    handle: string
    refetchCurrentUser: () => void
  },
    ref) => {
    const history = useHistory()
    const context = useContext(SessionContext)
    const [email, setEmail] = useState(context.user?.email || '')
    const [emailError, setEmailError] = useState<string>('')
    const [createAccount, setCreateAccount] = useState(true)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [state, setState] = useState('signup')
    const modalRef = useRef<any>(null)

    const [createBasicUser, { loading: loadingCreateBasicUser }] = useMutation(CREATE_BASIC_USER, {
      onError: error => {
        if (error.message.includes('Email has already been taken')) {
          emailValue(email)
          history.push(buildPath('loginEmail'))
        } else {
          graphQLErrorHandler(setEmailError)(error)
        }
      },
      onCompleted: ({ createBasicUser }) => {
        const { user } = createBasicUser

        setRegistrationAuthenticated({
          token: createBasicUser.webAuthToken,
          isGuest: !createAccount,
          done: () => {
            context.logIn(user)
            track('FollowerAccountCreated', { email, userUuid: user.uuid })
            emailValue(email)
            userUuidValue(user.uuid)
            followFund({
              variables: {
                input: {
                  handle
                }
              }
            })
          },
        })
      }
    })

    const [followFund, { loading: loadingFollowFund }] = useMutation(
      FOLLOW_FUND, {
      onCompleted: () => {
        setState('success')
        refetchCurrentUser()
      },
      onError: error => {
        setErrorMessage(error.message)
        setState('error')
      }
    })

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const errors: ErrorMessages = validateEmail({ email })
      setEmailError(errors['email'] || '')

      if (isEmpty(errors)) {
        const input = prepareRegistrationParams({
          email: email.trim(),
          guest: createAccount ? 'never' : 'current'
        })

        createBasicUser({ variables: { input } })
      }
    }

    useImperativeHandle(ref, () => ({
      open() {
        modalRef?.current?.open()
        if (context.isAuthenticated) {
          followFund({
            variables: {
              input: {
                handle
              }
            }
          })
        }
      }
    }));

    return (
      <Modal
        ref={modalRef}
        contentClassName="typo-app-body-large-paragraph"

      >
        {(loadingCreateBasicUser || loadingFollowFund) ? (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" />
          </div>
        ) : (<>
          {state === 'signup' && (<div className={styles.setupContainer}>
            <img src={birthdayReminder} alt='' className={styles.image} />
            <h3 className={styles.signupTitle}>
              Follow this fund
            </h3>
            <p className="typo-app-body-paragraph">
              We’ll send you birthday reminders and other occasional updates so you can stay connected.
            </p>
            <form
              className={styles.signupForm}
              method="post"
              onSubmit={handleSubmit}>
              <Input
                autoFocus={!isMobile()}
                label="Your email"
                name="email"
                type="email"
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                errorMessage={emailError}
                className={styles.input}
              />
              <Checkbox
                name="createAccount"
                id="createAccount"
                checked={createAccount}
                onChange={(checked: boolean) => setCreateAccount(checked)}
                className={styles.checkbox}
              >
                <span className="typo-app-body-main">
                  Create a Backer account to track your contributions and receive fund updates.
                </span>
              </Checkbox>
              <Button
                type="submit"
                label="Sign me up!"
                disabled={!email.trim()}
                className={styles.signupButton}
              />
              {renderLegalese()}
            </form>
          </div>)}
          {state === 'success' && (<div className={styles.successContainer}>
            <h3 className="typo-app-title-medium">
              Awesome! You're all set 🎉
            </h3>
            <p className="typo-app-body-paragraph">
              We’ll send birthday reminders and fund updates to {email}.
            </p>
            <Button
              type="submit"
              label="Great"
              className={styles.successButton}
              onClick={() => {
                modalRef.current.close()
              }}
            />
          </div>)}
          {state === 'error' && <WarnMesssage text={errorMessage} type="warning" />}
        </>)}
      </Modal>
    )
  })

export default BirthdayReminderModal
